import { useState, useEffect, useMemo } from 'react';
import {
    Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Typography, Autocomplete, TextField, Box,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { globalPostService, globalPutService } from '../../../utils/globalApiServices';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { checkApiStatus } from '../../../utils';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import NumericLabel from 'react-pretty-numbers';
import { changeBulkInvoiceStatusApi, getInvoiceConstant } from '../apiService';
import { PageLoader } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';

const BulkChangeInvoiceStatus = ({ selectedInvoices, onClose, getResponseBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [invoiceConstants, setInvoiceConstants] = useState([]);
    const [error, setError] = useState({});

    // All selected invoices have the same status (enforced by selection logic)
    const currentStatusValue = selectedInvoices[0]?.status?.value;

    const [invoice, setInvoice] = useState({
        status: selectedInvoices[0]?.status || null,
        sent_date: moment().format(backendDateFormat),
        payment_date: null,
    });

    // Only valid transition targets: Draft(1)→Sent(2), Sent(2)→Paid(3)
    const filteredStatuses = invoiceConstants.filter(s =>
        currentStatusValue === 1 ? s.value === 2 :
        currentStatusValue === 2 ? s.value === 3 : false
    );

    // Latest invoice date among selected Draft invoices — Sent Date cannot be before this
    const maxInvoiceDate = useMemo(() => {
        if (currentStatusValue !== 1) return null;
        const dates = selectedInvoices.map(inv => inv.date).filter(Boolean).map(d => moment(d));
        return dates.length > 0 ? moment.max(dates) : null;
    }, [selectedInvoices, currentStatusValue]);

    // Latest sent_date among selected Sent invoices — Payment Date cannot be before this
    const maxSentDate = useMemo(() => {
        if (currentStatusValue !== 2) return null;
        const dates = selectedInvoices.map(inv => inv.sent_date).filter(Boolean).map(d => moment(d));
        return dates.length > 0 ? moment.max(dates) : null;
    }, [selectedInvoices, currentStatusValue]);

    const currencySummary = useMemo(() => {
        const map = {};
        selectedInvoices.forEach(inv => {
            const code = inv.currency?.code || 'USD';
            if (!map[code]) map[code] = { code, total: 0, count: 0 };
            if(inv?.invoice_type?.value===5) map[code].total -= parseFloat(inv.amount || 0);
            else map[code].total += parseFloat(inv.amount || 0);
            map[code].count += 1;
        });
        return Object.values(map);
    }, [selectedInvoices]);

    useEffect(() => {
        setLoading(true);
        globalPostService(getInvoiceConstant(), { constant_types: ['invoice_status'] }).then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                setInvoiceConstants(response?.data?.data || []);
            }
        });
    }, []);

    const onFieldChange = (key, value) => {
        setInvoice(prev => ({ ...prev, [key]: value }));
    };

    const onRestErrorKey = (key) => {
        setError(prev => ({ ...prev, [key]: '' }));
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Bulk Status Change', page_type: page_type });
    };

    const isDataChanged = () => invoice?.status?.value !== currentStatusValue;

    const validate = () => {
        const validationInputs = {};
        const statusValue = invoice?.status?.value;

        if (statusValue === 2) {
            const sd = invoice?.sent_date;
            if (!sd) {
                validationInputs.sent_date = 'Sent Date is required.';
            } else {
                const m = moment(sd, backendDateFormat, true);
                if (!m.isValid()) {
                    validationInputs.sent_date = 'Please enter a valid date.';
                } else if (m.isAfter(moment())) {
                    validationInputs.sent_date = 'Sent Date cannot be in the future.';
                } else if (maxInvoiceDate && m.isBefore(maxInvoiceDate, 'day')) {
                    validationInputs.sent_date = `Sent Date cannot be earlier than the latest Invoice Date of selected invoices (${maxInvoiceDate.format(fieldDateFormat)}).`;
                }
            }
        }

        if (statusValue === 3) {
            const pd = invoice?.payment_date;
            if (!pd) {
                validationInputs.payment_date = 'Payment Date is required.';
            } else {
                const m = moment(pd, backendDateFormat, true);
                if (!m.isValid()) {
                    validationInputs.payment_date = 'Please enter a valid date.';
                } else if (m.isAfter(moment())) {
                    validationInputs.payment_date = 'Payment Date cannot be in the future.';
                } else if (maxSentDate && m.isBefore(maxSentDate, 'day')) {
                    validationInputs.payment_date = `Payment Date cannot be earlier than the latest Sent Date of selected invoices (${maxSentDate.format(fieldDateFormat)}).`;
                }
            }
        }

        setError(validationInputs);
        return Object.keys(validationInputs).every(k => !validationInputs[k]);
    };

    const onSubmit = () => {
        if (!validate()) return;

        const payload = {
            slug: selectedInvoices.map(inv => inv.slug),
            status: invoice.status,
        };

        if (invoice.status?.value === 2) {
            payload.sent_date = invoice.sent_date;
        }
        if (invoice.status?.value === 3) {
            payload.payment_date = invoice.payment_date;
        }

        setLoading(true);
        globalPutService(changeBulkInvoiceStatusApi(), payload).then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data?.message || 'Invoice status updated successfully', {
                    variant: 'success',
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
                trackActivity('Bulk Invoice Status Changed', {
                    page_title: 'Fleet Invoices',
                    total: selectedInvoices.length,
                    new_status: invoice.status?.value,
                    success_msg: response?.data?.message,
                    payload
                });
                getResponseBack();
                onClose();
            } else {
                enqueueSnackbar(response?.data?.message || 'Failed to update invoice status', {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
                 trackActivity('Bulk Invoice Status Changed', {
                    page_title: 'Fleet Invoices',
                    total: selectedInvoices.length,
                    new_status: invoice.status?.value,
                    error_msg: response?.data?.message,
                    payload
                });
            }
        });
    };

    return (
        <>
            <Dialog open maxWidth="md" aria-labelledby="bulk-status-dialog-title" className="add-lease-dialog">
                <DialogTitle sx={{ m: 0, p: 2 , display: 'flex', alignItems: 'center', justifyContent: 'space-between', }} className="dialog-header" id="bulk-status-dialog-title">
                    Change Invoice Status
                    <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', px: 1.2, py: 0.3, borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                        {selectedInvoices.length} Invoice{selectedInvoices.length > 1 ? 's' : ''}
                    </Box>
                </DialogTitle>
                <DialogContent dividers style={{ minWidth: '400px', maxWidth: [2, 3].includes(invoice?.status?.value) ? '560px' : '400px', minHeight: '100px', padding: '16px' }}>
                    <Grid container spacing={2}>
                        <Grid size={{ md: invoice?.status?.value === 3 ? 6 : 12, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                options={filteredStatuses}
                                getOptionLabel={(option) => option.label || ''}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={invoice?.status}
                                disableClearable
                                onFocus={() => onRestErrorKey('status')}
                                onChange={(e, newValue) => {
                                    setInvoice(prev => ({ ...prev, status: newValue, payment_date: null }));
                                    setError({});
                                    handleItemClicked(`Status Selected: ${newValue?.label}`);
                                }}
                                slotProps={{
                                    paper: { sx: { maxHeight: 90, overflowY: 'auto' } },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        required
                                        {...params}
                                        label="Status"
                                        margin="normal"
                                        size="small"
                                        helperText={error.status}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                )}
                            />
                        </Grid>

                        {invoice?.status?.value === 2 && currentStatusValue === 1 && (
                            <Grid size={{ md: 12, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Sent Date"
                                        value={invoice?.sent_date ? moment(invoice.sent_date) : null}
                                        onChange={(data) => {
                                            const isValid = moment(data)?.isValid();
                                            const formatted = isValid ? moment(data).format(backendDateFormat) : null;
                                            onFieldChange('sent_date', formatted);
                                            if (isValid) onRestErrorKey('sent_date');
                                        }}
                                        disableFuture
                                        minDate={maxInvoiceDate || undefined}
                                        slotProps={{
                                            textField: {
                                                required: true,
                                                InputLabelProps: { shrink: true },
                                                size: 'small',
                                                margin: 'normal',
                                                error: !!error.sent_date,
                                                helperText: error.sent_date || '',
                                                onFocus: () => onRestErrorKey('sent_date'),
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { onFieldChange('sent_date', null); onRestErrorKey('sent_date'); },
                                            },
                                        }}
                                        sx={{ width: '100%' }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}

                        {invoice?.status?.value === 2 && currentStatusValue === 1 && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px' }}>
                                        Selected Invoices
                                    </Typography>
                                    <Tooltip
                                        title={
                                            maxInvoiceDate
                                                ? `Sent Date must be on or after ${maxInvoiceDate.format(fieldDateFormat)} — the latest Invoice Date among selected invoices.`
                                                : 'Sent Date cannot be in the future.'
                                        }
                                        placement="right"
                                        arrow
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: '15px', color: '#64748b', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <TableContainer component={Paper} className="lease-detail-table" sx={{ maxHeight: 185, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader className="mui-table-format">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Invoice Number</TableCell>
                                                <TableCell>Invoice Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...selectedInvoices]
                                                .sort((a, b) => moment(b.date).diff(moment(a.date)))
                                                .map((inv, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{inv.invoice_number || '--'}</TableCell>
                                                        <TableCell>{inv.date ? moment(inv.date).format(fieldDateFormat) : '--'}</TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {invoice?.status?.value === 3 && (
                            <Grid size={{ md: 6, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Payment Date"
                                        value={invoice?.payment_date ? moment(invoice.payment_date) : null}
                                        onChange={(data) => {
                                            const isValid = moment(data)?.isValid();
                                            const formatted = isValid ? moment(data).format(backendDateFormat) : null;
                                            onFieldChange('payment_date', formatted);
                                            if (isValid) onRestErrorKey('payment_date');
                                        }}
                                        disableFuture
                                        minDate={maxSentDate || undefined}
                                        slotProps={{
                                            textField: {
                                                required: true,
                                                InputLabelProps: { shrink: true },
                                                size: 'small',
                                                margin: 'normal',
                                                error: !!error.payment_date,
                                                helperText: error.payment_date || '',
                                                onFocus: () => onRestErrorKey('payment_date'),
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () => { onFieldChange('payment_date', null); onRestErrorKey('payment_date'); },
                                            },
                                        }}
                                        sx={{ width: '100%' }}
                                        format={fieldDateFormat}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}

                        {invoice?.status?.value === 3 && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '13px' }}>
                                        Selected Invoices
                                    </Typography>
                                    <Tooltip
                                        title={
                                            maxSentDate
                                                ? `Payment Date must be on or after ${maxSentDate.format(fieldDateFormat)} — the latest Sent Date among selected invoices.`
                                                : 'Payment Date cannot be in the future.'
                                        }
                                        placement="right"
                                        arrow
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: '15px', color: '#64748b', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Box>
                                <TableContainer component={Paper} className="lease-detail-table" sx={{ maxHeight: 185, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader className="mui-table-format">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Invoice Number</TableCell>
                                                <TableCell>Sent Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...selectedInvoices]
                                                .sort((a, b) => moment(b.sent_date).diff(moment(a.sent_date)))
                                                .map((inv, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{inv.invoice_number || '--'}</TableCell>
                                                        <TableCell>{inv.sent_date ? moment(inv.sent_date).format(fieldDateFormat) : '--'}</TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {invoice?.status?.value === 3 && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ mt: 0.5 }}>
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '14px' }}>
                                            Total Paid Amount
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {currencySummary.map(cur => (
                                            <Box
                                                key={cur.code}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 2,
                                                    py: 1.2,
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    bgcolor: '#ffffff',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                                }}
                                            >
                                                <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', px: 1.2, py: 0.4, borderRadius: '6px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.04em' }}>
                                                    {cur.code}
                                                </Box>
                                                <Typography sx={{ fontWeight: 700, color: cur?.total < 0 ? "#FF0000" : "#16a34a", fontSize: '15px', letterSpacing: '0.01em' }}>
                                                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: cur.code, precision: 2, commafy: true, justification: 'L' }}>
                                                        {cur.total}
                                                    </NumericLabel>
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ paddingRight: '16px' }}>
                    <Button className="cancel-btn" variant="outlined" size="small" disabled={isLoading} onClick={() => { onClose(); handleItemClicked('Cancel Button') }}>
                        Cancel
                    </Button>
                    <Button variant="contained" className="btn-primary" size="small" disabled={isLoading || !isDataChanged()} onClick={() => { onSubmit(); handleItemClicked('Save Button') }} >
                        {isLoading ? <CircularProgress color="inherit" size={25} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading && <PageLoader />}
        </>
    );
};

export default BulkChangeInvoiceStatus;
